import { BadgeTone } from "stories/Admin";

/** En TV-skjerm koblet (eller på vei til å bli koblet) til en moské med en 4-sifret kode. */
export interface TvDevice {
  id: number;
  orgNr: string;
  mosqueName?: string | null;
  name: string;
  status: "pending" | "active" | "expired" | "revoked";
  code?: string | null;
  codeExpiresAt?: string | null;
  tokenPrefix?: string | null;
  createdByEmail?: string | null;
  createdAt: string;
  activatedAt?: string | null;
  lastSeenAt?: string | null;
  lastIp?: string | null;
  lastUserAgent?: string | null;
  appVersion?: string | null;
  platform?: string | null;
  deviceModel?: string | null;
  revokedAt?: string | null;
  /**
   * Om serveren kjenner igjen TV-en uten nyere TV-app: ingen annen TV i
   * moskeen har samme IP-adresse og app-type. Mangler på eldre servere.
   */
  recognizable?: boolean;
  /** Utseendet admin har valgt. Null = TV-en velger selv. Mangler på eldre servere. */
  displayMode?: DisplayMode | null;
  /** Det TV-en sist sa at den viste: en visning, eller menyen «Velg visning». */
  shownScreen?: "display" | "modes" | null;
  shownMode?: DisplayMode | null;
  shownAt?: string | null;
}

export type DisplayMode = "STANDARD" | "FOCUS" | "DAYLIGHT" | "NEXT";

/** Utseendene på TV-en, som i TV-appene. */
export const DISPLAY_MODES: Array<{ value: DisplayMode; label: string }> = [
  { value: "STANDARD", label: "Standard" },
  { value: "FOCUS", label: "Fokus" },
  { value: "DAYLIGHT", label: "Dagslys" },
  { value: "NEXT", label: "Neste bønn" },
];

export const modeLabel = (mode?: string | null) =>
  DISPLAY_MODES.find((m) => m.value === mode)?.label ?? null;

/**
 * Nyere TV-apper (WebTV) sier hvert 15. sekund hva de viser. Har TV-en sagt det det siste minuttet, er den på
 * akkurat nå og kan forhåndsvises.
 */
export const LIVE_SECONDS = 60;

export const isLive = (d: TvDevice, now = Date.now()) =>
  d.status === "active" &&
  !!d.shownAt &&
  !!d.lastSeenAt &&
  now - new Date(d.shownAt).getTime() < LIVE_SECONDS * 1000 &&
  now - new Date(d.lastSeenAt).getTime() < LIVE_SECONDS * 1000;

/**
 * TV-ene som er på nå: de som melder hva de viser (det siste minuttet), og TV-apper som ikke melder det ennå og
 * har pinget de siste 15 minuttene. En TV som har meldt før, men ikke det siste minuttet, er slått av.
 */
export const isOnNow = (d: TvDevice, now = Date.now()) =>
  isLive(d, now) || (isOnline(d, now) && !d.shownAt);

/** «Viser Fokus», «Står på «Velg visning»», eller null når TV-appen ikke sier det. */
export const nowShowing = (d: TvDevice, now = Date.now()) => {
  if (!isLive(d, now)) return null;
  if (d.shownScreen === "modes") return "Står på «Velg visning»";
  return `Viser ${modeLabel(d.shownMode) ?? "Standard"}`;
};

/** En TV som har pinget de siste 15 minuttene regnes som på. */
export const ONLINE_MINUTES = 15;

export const isOnline = (d: TvDevice, now = Date.now()) =>
  d.status === "active" &&
  !!d.lastSeenAt &&
  now - new Date(d.lastSeenAt).getTime() < ONLINE_MINUTES * 60_000;

export const statusBadge = (
  d: TvDevice,
  now = Date.now(),
): { tone: BadgeTone; label: string } => {
  if (d.status === "revoked") return { tone: "grey", label: "Koblet fra" };
  if (d.status === "active")
    return isOnline(d, now)
      ? { tone: "mint", label: "På" }
      : { tone: "blue", label: "Tilkoblet" };
  if (d.status === "pending") return { tone: "gold", label: "Venter på koden" };
  return { tone: "rose", label: "Koden gikk ut" };
};

/** «for 5 min siden», «for 3 t siden», ellers dato og klokkeslett. */
export const formatAgo = (value?: string | null, now = Date.now()) => {
  if (!value) return "Aldri";
  const minutes = Math.floor((now - new Date(value).getTime()) / 60_000);
  if (minutes < 1) return "Akkurat nå";
  if (minutes < 60) return `For ${minutes} min siden`;
  if (minutes < 24 * 60) return `For ${Math.floor(minutes / 60)} t siden`;
  return new Date(value).toLocaleString("nb-NO", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleTimeString("nb-NO", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "–";

/** Plattform, modell og app-versjon, det TV-en har fortalt om seg selv. */
export const deviceDescription = (d: TvDevice) =>
  [d.platform, d.deviceModel, d.appVersion && `v${d.appVersion}`]
    .filter(Boolean)
    .join(" · ");
